import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { DataTable, DataTableColumn } from '../../components/ui/data-table';
import StatsCard from '../../components/common/StatsCard';
import { Users, DollarSign, Activity, TrendingUp } from 'lucide-react';
import UserActivityChart from '../../components/charts/UserActivityChart';
import UserDemographicsChart from '../../components/charts/UserDemographicsChart';
import TradeVolumeChart from '../../components/charts/TradeVolumeChart';
import { capitalizeFirst } from '../../utils/helpers';
import PageHeader from '../../components/common/PageHeader';

const AdminDashboard = () => {
  const userActivityData = [
    { date: 'Mon', activeUsers: 120, newUsers: 25 },
    { date: 'Tue', activeUsers: 145, newUsers: 30 },
    { date: 'Wed', activeUsers: 135, newUsers: 20 },
    { date: 'Thu', activeUsers: 160, newUsers: 35 },
    { date: 'Fri', activeUsers: 150, newUsers: 28 },
    { date: 'Sat', activeUsers: 110, newUsers: 15 },
    { date: 'Sun', activeUsers: 95, newUsers: 10 },
  ];

  const demographicsData = [
    { name: 'Asia', value: 45, color: '#3b82f6' },
    { name: 'Europe', value: 30, color: '#10b981' },
    { name: 'North America', value: 20, color: '#f59e0b' },
    { name: 'Other', value: 5, color: '#8b5cf6' },
  ];

  const tradeVolumeData = [
    { date: 'Jan', volume: 45000, trades: 120 },
    { date: 'Feb', volume: 52000, trades: 145 },
    { date: 'Mar', volume: 48000, trades: 135 },
    { date: 'Apr', volume: 61000, trades: 160 },
    { date: 'May', volume: 55000, trades: 150 },
    { date: 'Jun', volume: 67000, trades: 110 },
  ];

  const recentTrades = [
    {
      id: 'TRD001',
      client: 'John Doe',
      symbol: 'EUR/USD',
      type: 'buy',
      volume: 0.1,
      profit: 25.0,
      time: '10:30',
    },
    {
      id: 'TRD002',
      client: 'Jane Smith',
      symbol: 'GBP/USD',
      type: 'sell',
      volume: 0.2,
      profit: 50.0,
      time: '09:15',
    },
    {
      id: 'TRD003',
      client: 'Bob Johnson',
      symbol: 'USD/JPY',
      type: 'buy',
      volume: 0.15,
      profit: -15.0,
      time: '08:20',
    },
  ];

  const tradeColumns: DataTableColumn<(typeof recentTrades)[0]>[] = [
    {
      id: 'id',
      header: 'Trade ID',
      accessor: row => <span className="font-medium">{row.id}</span>,
      sortable: true,
      sortValue: row => row.id,
      width: '120px',
    },
    {
      id: 'client',
      header: 'Client',
      accessor: row => row.client,
      sortable: true,
      sortValue: row => row.client,
      width: '150px',
    },
    {
      id: 'symbol',
      header: 'Symbol',
      accessor: row => row.symbol,
      sortable: true,
      sortValue: row => row.symbol,
      width: '120px',
    },
    {
      id: 'type',
      header: 'Type',
      accessor: row => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.type === 'buy'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {capitalizeFirst(row.type)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.type,
      width: '100px',
    },
    {
      id: 'volume',
      header: 'Volume',
      accessor: row => row.volume,
      sortable: true,
      sortValue: row => row.volume,
      width: '100px',
    },
    {
      id: 'profit',
      header: 'Profit',
      accessor: row => (
        <span className={row.profit >= 0 ? 'text-green-600' : 'text-red-600'}>
          ${row.profit.toFixed(2)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.profit,
      width: '120px',
    },
    {
      id: 'time',
      header: 'Time',
      accessor: row => row.time,
      sortable: true,
      sortValue: row => row.time,
      width: '100px',
    },
  ];

  const recentClients = [
    {
      id: 'CLT001',
      name: 'Alice Williams',
      email: 'alice@example.com',
      status: 'active',
      joined: '2024-01-15',
    },
    {
      id: 'CLT002',
      name: 'Charlie Brown',
      email: 'charlie@example.com',
      status: 'pending',
      joined: '2024-01-16',
    },
    {
      id: 'CLT003',
      name: 'Diana Miller',
      email: 'diana@example.com',
      status: 'active',
      joined: '2024-01-14',
    },
  ];

  const clientColumns: DataTableColumn<(typeof recentClients)[0]>[] = [
    {
      id: 'id',
      header: 'Client ID',
      accessor: row => <span className="font-medium">{row.id}</span>,
      sortable: true,
      sortValue: row => row.id,
      width: '120px',
    },
    {
      id: 'name',
      header: 'Name',
      accessor: row => row.name,
      sortable: true,
      sortValue: row => row.name,
      width: '180px',
    },
    {
      id: 'email',
      header: 'Email',
      accessor: row => row.email,
      sortable: true,
      sortValue: row => row.email,
      width: '200px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.status === 'active'
              ? 'bg-green-100 text-green-800'
              : row.status === 'pending'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-gray-100 text-gray-800'
          }`}
        >
          {capitalizeFirst(row.status)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.status,
      width: '120px',
    },
    {
      id: 'joined',
      header: 'Joined',
      accessor: row => row.joined,
      sortable: true,
      sortValue: row => row.joined,
      width: '120px',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Admin Dashboard"
          description="Overview of your Forex CRM system"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Clients"
          value="1,234"
          change="+12% from last month"
          icon={Users}
        />

        <StatsCard
          title="Active Trades"
          value="456"
          change="+8% from yesterday"
          icon={Activity}
        />

        <StatsCard
          title="Total Volume"
          value="$45.2M"
          change="+15% from last month"
          icon={DollarSign}
        />

        <StatsCard
          title="Revenue"
          value="$125K"
          change="+20% from last month"
          icon={TrendingUp}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <UserActivityChart data={userActivityData} />
        <UserDemographicsChart data={demographicsData} />
        <TradeVolumeChart data={tradeVolumeData} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Recent Trades</CardTitle>
            <CardDescription>Latest trading activities</CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={tradeColumns}
              data={recentTrades}
              searchPlaceholder="Search trades..."
              showColumnVisibility={true}
              showExportButton={false}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Clients</CardTitle>
            <CardDescription>Latest client registrations</CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={clientColumns}
              data={recentClients}
              searchPlaceholder="Search clients..."
              showColumnVisibility={true}
              showExportButton={false}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
